import React from "react"
import PropTypes from "prop-types"

/**
 * ProgressIndicator
 *
 * Displays progress through a multi-step flow with multiple visual variants.
 *
 * @param {Object} props
 * @param {number} props.currentStep - Current step index (0-based)
 * @param {number} props.totalSteps - Total number of steps
 * @param {boolean} props.showStepText - Whether to show "Step X out of Y" text (default: false)
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.stepProgressModifier - Additional modifier to apply to the progress bar (e.g., to show progress one step ahead)
 */
const ProgressIndicator = ({
  currentStep,
  totalSteps,
  showStepText = false,
  className = "",
  stepProgressModifier = 0,
}) => {
  const progressPercentage = ((currentStep + stepProgressModifier) / totalSteps) * 100

  return (
    <div className={`flex w-full flex-col gap-2 ${className}`}>
      {showStepText && (
        <div className="text-center text-xs text-brandSecondary">
          {progressPercentage >= 100 ? null : `Step ${currentStep + 1} out of ${totalSteps}`}
        </div>
      )}
      <div className="flex flex-row items-center gap-2">
        <div className="text-xs text-brandSecondary">{progressPercentage.toFixed(0)}%</div>
        <div className="h-fit w-full rounded-full border border-brandSecondary bg-white">
          <div
            className="h-2 rounded-full bg-brandSecondary transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
    </div>
  )
}

ProgressIndicator.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  showStepText: PropTypes.bool,
  className: PropTypes.string,
  stepProgressModifier: PropTypes.number,
}

export default ProgressIndicator
